import React, { useState } from 'react'
import {
  Box, Button, Checkbox, Chip, MenuItem, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Toolbar, Typography
} from '@material-ui/core'
import axios from 'axios'

const CAT_COLORS = {
  'Irrigation': '#3b82f6', 'Planting': '#10b981', 'Hardscape': '#6366f1',
  'Earthwork': '#d97706', 'Demo': '#ef4444', 'Lighting': '#f59e0b',
  'Chemical': '#8b5cf6', 'Turf': '#84cc16', 'Signage': '#06b6d4',
  'General': '#6b7280'
}

const CATEGORIES = Object.keys(CAT_COLORS)

const CategoryChip = ({ category, onClick }) => {
  if (!category) return <Chip label="Uncategorized" size="small" variant="outlined" onClick={onClick} style={{ cursor: 'pointer' }} />
  const color = CAT_COLORS[category] || '#6b7280'
  return (
    <Chip
      label={category}
      size="small"
      onClick={onClick}
      style={{ backgroundColor: color, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 11 }}
    />
  )
}

const CategoryStage = ({ assemblies, url, projectId, bidPackages, onAutoCategorize, onRefresh }) => {
  const [selected, setSelected] = useState(new Set())
  const [bulkCategory, setBulkCategory] = useState('')
  const [filterCat, setFilterCat] = useState('all')

  const API = `${url}/api/bid-estimation`

  const filtered = filterCat === 'all'
    ? assemblies
    : filterCat === 'uncategorized'
      ? assemblies.filter(a => !a.category)
      : assemblies.filter(a => a.category === filterCat)

  const toggleSelect = (id) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(filtered.map(a => a._id)))
    }
  }

  const handleBulkCategorize = async () => {
    if (!bulkCategory || selected.size === 0) return
    try {
      await axios.post(`${API}/assembly/bulk-categorize`, {
        assemblyIds: [...selected],
        category: bulkCategory
      })
      setSelected(new Set())
      setBulkCategory('')
      onRefresh()
    } catch (err) {
      console.error('Bulk categorize failed:', err)
    }
  }

  const handleInlineCategory = async (assemblyId, category) => {
    try {
      await axios.post(`${API}/assembly/bulk-categorize`, {
        assemblyIds: [assemblyId],
        category
      })
      onRefresh()
    } catch (err) {
      console.error('Inline categorize failed:', err)
    }
  }

  return (
    <Box>
      {/* Toolbar */}
      <Toolbar variant="dense" disableGutters style={{ gap: 8, flexWrap: 'wrap', minHeight: 48, marginBottom: 8 }}>
        {bidPackages.map(pkg => (
          <Button
            key={pkg._id}
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onAutoCategorize(pkg._id)}
            style={{ textTransform: 'none', fontSize: 12 }}
          >
            Auto-Categorize: {pkg.name || 'Package'}
          </Button>
        ))}
        <Box flex={1} />
        <Select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 140, height: 32, fontSize: 13 }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          <MenuItem value="uncategorized">Uncategorized</MenuItem>
          {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </Toolbar>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <Box display="flex" alignItems="center" gap={1} mb={1} p={1} bgcolor="#e3f2fd" borderRadius={4} style={{ gap: 8 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {selected.size} selected
          </Typography>
          <Select
            value={bulkCategory}
            onChange={e => setBulkCategory(e.target.value)}
            displayEmpty
            variant="outlined"
            size="small"
            style={{ minWidth: 140, height: 32, fontSize: 13 }}
          >
            <MenuItem value="" disabled>Set category...</MenuItem>
            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleBulkCategorize}
            disabled={!bulkCategory}
            style={{ textTransform: 'none' }}
          >
            Apply
          </Button>
        </Box>
      )}

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.size > 0 && selected.size === filtered.length}
                  indeterminate={selected.size > 0 && selected.size < filtered.length}
                  onChange={toggleAll}
                  size="small"
                />
              </TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12 }}>#</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Description</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12 }} align="right">Qty</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Unit</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Type</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Category</TableCell>
              <TableCell style={{ fontWeight: 700, fontSize: 12 }} align="right">Confidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(asm => {
              const bi = asm.bidItem || {}
              return (
                <TableRow key={asm._id} hover selected={selected.has(asm._id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(asm._id)}
                      onChange={() => toggleSelect(asm._id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: 12 }}>{bi.itemNo || '-'}</TableCell>
                  <TableCell style={{ fontSize: 12, maxWidth: 400 }}>
                    {bi.descriptionRaw || bi.descriptionNormalized || '-'}
                  </TableCell>
                  <TableCell align="right" style={{ fontSize: 12 }}>{bi.quantity ?? '-'}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>{bi.unitRaw || bi.unitNormalized || '-'}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>{bi.itemType || '-'}</TableCell>
                  <TableCell>
                    <CategoryChip
                      category={asm.category}
                      onClick={() => {
                        // Cycle through categories on click
                        const idx = CATEGORIES.indexOf(asm.category)
                        const next = CATEGORIES[(idx + 1) % CATEGORIES.length]
                        handleInlineCategory(asm._id, next)
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" style={{ fontSize: 12 }}>
                    {bi.categoryConfidence != null ? `${Math.round((bi.categoryConfidence || 0) * 100)}%` : '-'}
                  </TableCell>
                </TableRow>
              )
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: 32, color: '#999' }}>
                  No bid items to display. Initialize assemblies from a bid package first.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default CategoryStage
